
import React, { useCallback } from "react";
import { Movie } from "@/types/movie";
import { Card, CardContent } from "@/components/ui/card";
import { MovieTitle, MovieYear, MovieDirector } from "@/components/ui/typography";
import MovieTags from "../MovieTags";
import { getThumbnailUrl } from "@/utils/imageUtils";
import { UnifiedActionButton } from "../buttons";
import { useUnifiedActionButtons } from "@/hooks/useUnifiedActionButtons";
import { getButtonContext } from "@/utils/buttonContextUtils";

interface HorizontalMovieCardProps {
  movie: Movie;
  variant: "list" | "search";
  currentTab?: string;
  isHovered: boolean;
  showAsFavorite?: boolean;
  showAsPlanToWatch?: boolean;
  handleOpenDialog: () => void;
  handleToggleFavorite?: (e: React.MouseEvent) => void;
  handleTogglePlanToWatch?: (e: React.MouseEvent) => void;
  setIsHovered: (value: boolean) => void;
}

const HorizontalMovieCard: React.FC<HorizontalMovieCardProps> = ({
  movie,
  variant,
  currentTab = 'favorites',
  isHovered,
  showAsFavorite = false,
  showAsPlanToWatch = false,
  handleOpenDialog,
  handleToggleFavorite,
  handleTogglePlanToWatch,
  setIsHovered
}) => {
  const { title, year, director, tags, tmdbId, favorite, planToWatch } = movie;
  const thumbnailUrl = getThumbnailUrl(movie);
  const buttonContext = getButtonContext(undefined, variant, variant === "search");
  
  // FIXED: Remove handler overrides - let useUnifiedActionButtons handle clicks internally
  const { getFavoriteButtonProps, getWatchlistButtonProps } = useUnifiedActionButtons({
    movie,
    context: buttonContext,
    currentTab
    // No longer passing onToggleFavorite or onTogglePlanToWatch overrides
  });
  
  const favoriteProps = getFavoriteButtonProps();
  const watchlistProps = getWatchlistButtonProps();
  
  return (
    <Card 
      className="hover:bg-mono-slateGray/10 bg-transparent max-w-2xl"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <CardContent className="p-2 md:p-4 flex items-center">
        {/* FIXED: Only the thumbnail and content area are clickable for navigation */}
        <div 
          className="w-14 h-20 flex-shrink-0 mr-3 md:mr-4 overflow-hidden rounded cursor-pointer"
          onClick={handleOpenDialog}
        >
          {thumbnailUrl ? (
            <img src={thumbnailUrl} alt={title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-mono-slateGray/50 flex items-center justify-center">
              <span className="text-xs text-mono-seasalt font-bold truncate px-1">{title}</span>
            </div>
          )}
        </div>
        
        {/* FIXED: Content area is also clickable for navigation */}
        <div 
          className="flex-grow py-1 cursor-pointer"
          onClick={handleOpenDialog}
        >
          <MovieTitle>
            {title}
          </MovieTitle>
          <MovieYear>
            {year}
            {director && variant === "search" && (
              <>
                {" • "}
                <MovieDirector>{director}</MovieDirector>
              </>
            )}
          </MovieYear>
          
          <MovieTags tags={tags || []} className="mt-1" maxTags={2} />
        </div>
        
        {/* FIXED: Action buttons are outside the clickable area and prevent bubbling */}
        <div className="flex-shrink-0 ml-1 md:ml-2 flex gap-3 self-center">
          <UnifiedActionButton
            intent="favorite"
            context={buttonContext}
            isActive={favoriteProps.isActive}
            isDeselected={favoriteProps.isDeselected}
            isHovered={favoriteProps.isHovered}
            onClick={favoriteProps.onClick}
            onMouseEnter={favoriteProps.onMouseEnter}
            onMouseLeave={favoriteProps.onMouseLeave}
            showLabel={false}
          />
          
          <UnifiedActionButton
            intent="watchlist"
            context={buttonContext}
            isActive={watchlistProps.isActive}
            isDeselected={watchlistProps.isDeselected}
            isHovered={watchlistProps.isHovered}
            onClick={watchlistProps.onClick}
            onMouseEnter={watchlistProps.onMouseEnter}
            onMouseLeave={watchlistProps.onMouseLeave}
            showLabel={false}
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default HorizontalMovieCard;
